/**
 * Schedules page: upcoming/history table, status chips (including queued),
 * edit/cancel actions, and HITL / workflow action lists.
 */
import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import ScheduleApi, { TheoSchedule, TheoScheduleAction } from "../apis/scheduleApi";
import HitlActionParamsEditor from "../shared/components/HitlActionParamsEditor";
import {
  isStructuredWorkflowResumeAction,
  isStructuredWorkflowResumeSchedule,
  isStructuredWorkflowStartAction,
  isStructuredWorkflowStartSchedule,
  scheduleActionConfig,
  sortSchedulesNewestFirst,
  structuredWorkflowResumeAction,
  workflowStartDisplayName,
} from "../shared/utils/scheduleKinds";
import {
  formatDateTimeInTimeZone,
  fromDateTimeLocalInTimeZone,
  getDefaultTimeZone,
  getTimeZoneOptions,
  toDateTimeLocalInTimeZone,
} from "../shared/utils/timezoneUtils";

const api = new ScheduleApi();

type ScheduleTab = "upcoming" | "history";

function statusColor(status?: string) {
  // schedule status chip: queued uses info to stay distinct from running
  if (status === "completed" || status === "succeeded") return "success";
  if (status === "failed") return "error";
  if (status === "running") return "warning";
  if (status === "queued") return "info";
  if (status === "canceled") return "default";
  return "info";
}

function normalizeScheduleTab(value: string | null): ScheduleTab {
  if (value === "history") return "history";
  return "upcoming";
}

function classify(schedule: TheoSchedule): ScheduleTab {
  // upcoming tab: scheduled, queued, and running stay here until terminal
  if (schedule.status === "failed" || schedule.status === "completed" || schedule.status === "canceled") return "history";
  return "upcoming";
}

const fieldSx = {
  "& .MuiInputBase-root": { fontFamily: "var(--theo-font-sans)" },
  "& .MuiInputLabel-root": { fontFamily: "var(--theo-font-sans)" },
};

function actionDisplayText(action: TheoScheduleAction) {
  const defaultLabel = isStructuredWorkflowStartAction(action)
    ? "Start workflow"
    : isStructuredWorkflowResumeAction(action)
      ? "Scheduled workflow step"
      : "Scheduled action";
  return String(action.action_description || "").trim() || defaultLabel;
}

function stripWorkflowResumePrefix(value: unknown) {
  return String(value || "")
    .replace(/^Resume workflow step:\s*/i, "")
    .trim();
}

function workflowScheduledWorkLabel(action?: TheoScheduleAction | null) {
  if (!action) return "";
  const config = scheduleActionConfig(action);
  return (
    stripWorkflowResumePrefix(config.phase_label) ||
    stripWorkflowResumePrefix((config.resume_payload as any)?.phase_label) ||
    stripWorkflowResumePrefix(action.action_description)
  );
}

function scheduleWorkItemCountLabel(schedule: TheoSchedule): string | null {
  const count = schedule.actions?.length || 0;
  return `${count} action${count === 1 ? "" : "s"}`;
}

function scheduledWorkDisplay(schedule: TheoSchedule) {
  if (isStructuredWorkflowStartSchedule(schedule)) {
    return workflowStartDisplayName(schedule);
  }
  const workflowAction = structuredWorkflowResumeAction(schedule);
  if (workflowAction) {
    return workflowScheduledWorkLabel(workflowAction) || "Workflow step";
  }
  return scheduleWorkItemCountLabel(schedule) || "-";
}

function ActionSummaryText({ action, index }: { action: TheoScheduleAction; index: number }) {
  const description = actionDisplayText(action);
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
      <Typography noWrap sx={{ fontWeight: 700, fontFamily: "var(--theo-font-sans)", fontSize: 14, lineHeight: "20px" }}>
        #{index + 1}
      </Typography>
      <Typography noWrap color="text.secondary" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, lineHeight: "18px", minWidth: 0 }}>
        {description}
      </Typography>
    </Stack>
  );
}

function getActionSourceLink(action: TheoScheduleAction) {
  const config = scheduleActionConfig(action);
  const ticketId = String(config.ticket_id ?? "").trim();
  if (ticketId) {
    return {
      to: `/supervisor/tickets/${encodeURIComponent(ticketId)}`,
      title: "Open source",
    };
  }
  const threadId = String(action.source_ai_thread_id || "").trim();
  if (threadId) {
    return {
      to: `/chat-assistant/${encodeURIComponent(threadId)}`,
      title: "Open source",
    };
  }
  return null;
}

function getScheduleSourceLink(schedule: TheoSchedule) {
  for (const action of schedule.actions || []) {
    const ticketId = String(scheduleActionConfig(action).ticket_id ?? "").trim();
    if (ticketId) {
      return {
        to: `/supervisor/tickets/${encodeURIComponent(ticketId)}`,
        label: ticketId,
      };
    }
  }
  return null;
}

function ScheduleSourceCell({ schedule }: { schedule: TheoSchedule }) {
  const source = getScheduleSourceLink(schedule);
  if (!source) return <Typography variant="body2" color="text.secondary">-</Typography>;
  return (
    <Link
      component={RouterLink}
      to={source.to}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700, overflowWrap: "anywhere" }}
    >
      {source.label}
    </Link>
  );
}

function ScheduleActionSourceIcon({ action }: { action: TheoScheduleAction }) {
  const source = getActionSourceLink(action);
  if (!source) return null;
  const stopToggle = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };
  return (
    <Tooltip title={source.title}>
      <IconButton
        component={RouterLink}
        to={source.to}
        target="_blank"
        rel="noopener noreferrer"
        size="small"
        aria-label={source.title}
        onClick={stopToggle}
        onMouseDown={stopToggle}
        onFocus={stopToggle}
        sx={{ color: "#71717A", flexShrink: 0 }}
      >
        <OpenInNewIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

function ScheduleActionListItem({
  action,
  index,
}: {
  action: TheoScheduleAction;
  index: number;
}) {
  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: 1,
        bgcolor: "#FFFFFF",
        px: 1.5,
        py: 1,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
        <ActionSummaryText action={action} index={index} />
        <Chip size="small" label={action.status || "pending"} color={statusColor(action.status) as any} />
        <ScheduleActionSourceIcon action={action} />
      </Stack>
      {action.last_error_message ? (
        <Typography color="error" sx={{ mt: 0.75, fontFamily: "var(--theo-font-sans)", fontSize: 12 }}>
          {action.last_error_message}
        </Typography>
      ) : null}
    </Box>
  );
}

function ScheduleActionAccordion({
  action,
  index,
  children,
  summaryEnd,
}: {
  action: TheoScheduleAction;
  index: number;
  children: React.ReactNode;
  summaryEnd?: React.ReactNode;
}) {
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: "8px !important",
        bgcolor: "#FFFFFF",
        overflow: "hidden",
        "&:before": { display: "none" },
        "&.Mui-expanded": { my: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 20, color: "#71717A" }} />}
        sx={{
          minHeight: "44px !important",
          px: 1.5,
          "& .MuiAccordionSummary-content": { my: "8px !important" },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%", minWidth: 0 }}>
          <ActionSummaryText action={action} index={index} />
          {summaryEnd}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ borderTop: "1px solid #E5E7EB", p: 1.5, bgcolor: "#FFFBF8" }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

function referenceBaseForAction(action: TheoScheduleAction, index: number) {
  const sourceCallId = String(action.source_call_id || "").trim();
  if (/^[A-Za-z0-9_-]+$/.test(sourceCallId)) {
    return `by_call_id.${sourceCallId}.output`;
  }
  return `actions.${index}.output`;
}

function buildReferenceOptions(actions: TheoScheduleAction[], currentIndex: number) {
  return actions.slice(0, currentIndex).flatMap((action, index) => {
    const labelPrefix = `#${index + 1} ${actionDisplayText(action)}`;
    const base = referenceBaseForAction(action, index);
    return [
      { label: `${labelPrefix} output.id`, value: `${base}.id` },
      { label: `${labelPrefix} output.email`, value: `${base}.email` },
      { label: `${labelPrefix} output.mail`, value: `${base}.mail` },
      { label: `${labelPrefix} full output`, value: base },
    ];
  });
}

const Schedules: React.FC = () => {
  const userTimeZone = getDefaultTimeZone();
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedScheduleId = searchParams.get("scheduleId");
  const tabParam = searchParams.get("tab");
  const [schedules, setSchedules] = React.useState<TheoSchedule[]>([]);
  const [tab, setTab] = React.useState<ScheduleTab>(() => normalizeScheduleTab(tabParam));
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<TheoSchedule | null>(null);
  const [form, setForm] = React.useState({
    title: "",
    description: "",
    scheduled_for: toDateTimeLocalInTimeZone(null, userTimeZone),
    timezone: userTimeZone,
    actions: [] as TheoScheduleAction[],
  });
  const [expandedScheduleId, setExpandedScheduleId] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const timezoneOptions = React.useMemo(() => getTimeZoneOptions(form.timezone), [form.timezone]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.listSchedules();
      setSchedules(Array.isArray(response.schedules) ? response.schedules : []);
    } catch (err: any) {
      setError(err?.message || "Failed to load schedules");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    if (linkedScheduleId) return;
    setTab(normalizeScheduleTab(tabParam));
  }, [linkedScheduleId, tabParam]);

  const navigateToTab = React.useCallback(
    (value: ScheduleTab) => {
      setTab(value);
      setExpandedScheduleId(null);
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.delete("scheduleId");
        next.set("tab", value);
        return next;
      });
    },
    [setSearchParams]
  );
  const handleTabChange = React.useCallback(
    (_event: React.SyntheticEvent, value: ScheduleTab) => {
      navigateToTab(value);
    },
    [navigateToTab]
  );
  const handleActiveTabClick = React.useCallback(
    (value: ScheduleTab) => {
      if (tab !== value) return;
      if (!linkedScheduleId && tabParam === value) return;
      navigateToTab(value);
    },
    [linkedScheduleId, navigateToTab, tab, tabParam]
  );

  const openCreate = () => {
    const timezone = getDefaultTimeZone();
    setEditing(null);
    setForm({
      title: "",
      description: "",
      scheduled_for: toDateTimeLocalInTimeZone(null, timezone),
      timezone,
      actions: [],
    });
    setDialogOpen(true);
  };

  const openEdit = (schedule: TheoSchedule) => {
    const timezone = getDefaultTimeZone();
    const actions = (schedule.actions || []).map((action) => ({
      id: action.id,
      schedule_id: action.schedule_id,
      position: action.position,
      action_name: action.action_name,
      action_description: action.action_description,
      config_json: action.config_json || null,
      params_json: action.params_json || {},
      arg_types_json: action.arg_types_json || {},
      resolved_args: action.resolved_args || {},
      source_ai_thread_id: action.source_ai_thread_id,
      source_message_id: action.source_message_id,
      source_call_id: action.source_call_id,
      status: action.status,
      last_action_run_id: action.last_action_run_id,
      last_error_message: action.last_error_message,
      last_result_json: action.last_result_json,
    }));
    setEditing(schedule);
    setForm({
      title: schedule.title || "",
      description: schedule.description || "",
      scheduled_for: toDateTimeLocalInTimeZone(schedule.scheduled_for, timezone),
      timezone,
      actions,
    });
    setDialogOpen(true);
  };

  React.useEffect(() => {
    if (!linkedScheduleId || schedules.length === 0) return;
    const schedule = schedules.find((item) => String(item.id) === String(linkedScheduleId));
    if (!schedule) return;
    setExpandedScheduleId(String(schedule.id));
    setTab(classify(schedule));
  }, [linkedScheduleId, schedules]);

  const moveAction = (index: number, delta: -1 | 1) => {
    setForm((prev) => {
      const actions = [...prev.actions];
      const nextIndex = index + delta;
      if (nextIndex < 0 || nextIndex >= actions.length) return prev;
      [actions[index], actions[nextIndex]] = [actions[nextIndex], actions[index]];
      return { ...prev, actions };
    });
  };

  const removeAction = (index: number) => {
    setForm((prev) => ({ ...prev, actions: prev.actions.filter((_a, i) => i !== index) }));
  };

  const updateActionParams = (index: number, params: Record<string, any>) => {
    setForm((prev) => ({
      ...prev,
      actions: prev.actions.map((action, actionIndex) =>
        actionIndex === index ? { ...action, params_json: params } : action
      ),
    }));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const actions = editing
        ? form.actions.map((action, index) => ({
            id: action.id,
            source_call_id: action.source_call_id,
            action_name: action.action_name,
            action_description: action.action_description,
            position: index,
            params_json:
              action.params_json && typeof action.params_json === "object" && !Array.isArray(action.params_json)
                ? action.params_json
                : {},
          }))
        : [];
      const payload: Partial<TheoSchedule> = {
        title: form.title,
        description: form.description,
        scheduled_for: fromDateTimeLocalInTimeZone(form.scheduled_for, form.timezone),
        timezone: form.timezone,
      };
      if (editing?.id) {
        payload.actions = actions;
        await api.updateSchedule(editing.id, payload);
      } else {
        await api.createSchedule(payload);
      }
      setDialogOpen(false);
      await load();
    } catch (err: any) {
      setError(err?.message || "Failed to save schedule");
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (schedule: TheoSchedule) => {
    if (!schedule.id) return;
    setError(null);
    try {
      await api.cancelSchedule(schedule.id);
      await load();
    } catch (err: any) {
      setError(err?.message || "Failed to cancel schedule");
    }
  };

  const visible = React.useMemo(() => {
    const filtered = schedules.filter((schedule) => classify(schedule) === tab);
    if (tab === "history") return sortSchedulesNewestFirst(filtered);
    return filtered;
  }, [schedules, tab]);
  const editingStructuredWorkflowResume = editing
    ? isStructuredWorkflowResumeSchedule({ ...editing, actions: form.actions })
    : false;
  const editingStructuredWorkflowStart = editing
    ? isStructuredWorkflowStartSchedule({ ...editing, actions: form.actions })
    : false;
  const hideHitlActionEditor = editingStructuredWorkflowResume || editingStructuredWorkflowStart;

  return (
    <Box sx={{ width: "100%", p: 3, minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab value="upcoming" label="Upcoming" onClick={() => handleActiveTabClick("upcoming")} />
          <Tab value="history" label="History" onClick={() => handleActiveTabClick("history")} />
        </Tabs>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={load} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: "none" }}>
            New schedule
          </Button>
        </Stack>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Paper elevation={0} sx={{ border: "1px solid #E5E7EB", borderRadius: 2, overflow: "hidden" }}>
        <Table size="small" stickyHeader sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "24%", fontWeight: 700 }}>Schedule</TableCell>
              <TableCell sx={{ width: "17%", fontWeight: 700 }}>Run time</TableCell>
              <TableCell sx={{ width: "11%", fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ width: "14%", fontWeight: 700 }}>Scheduled work</TableCell>
              <TableCell sx={{ width: "10%", fontWeight: 700 }}>Source</TableCell>
              <TableCell sx={{ width: "14%", fontWeight: 700 }}>Owner</TableCell>
              <TableCell sx={{ width: "10%", fontWeight: 700 }} align="right">Manage</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((schedule) => {
              const canExpandSchedule =
                !isStructuredWorkflowResumeSchedule(schedule) && !isStructuredWorkflowStartSchedule(schedule);
              return (
                <React.Fragment key={String(schedule.id)}>
              <TableRow hover>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {canExpandSchedule ? (
                      <Tooltip title="Review actions">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setExpandedScheduleId((current) =>
                              current === String(schedule.id) ? null : String(schedule.id)
                            )
                          }
                        >
                          {expandedScheduleId === String(schedule.id) ? (
                            <KeyboardArrowUpIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowDownIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Box sx={{ width: 32, height: 32, flexShrink: 0 }} />
                    )}
                    <Typography sx={{ fontWeight: 700, fontFamily: "var(--theo-font-sans)" }}>{schedule.title}</Typography>
                  </Stack>
                  {canExpandSchedule && schedule.description ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {schedule.description}
                    </Typography>
                  ) : null}
                  {schedule.error_message ? (
                    <Typography variant="caption" color="error" sx={{ display: "block" }}>
                      {schedule.error_message}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{formatDateTimeInTimeZone(schedule.scheduled_for, userTimeZone)}</TableCell>
                <TableCell>
                  <Chip size="small" label={schedule.status || "scheduled"} color={statusColor(schedule.status) as any} />
                </TableCell>
                <TableCell>{scheduledWorkDisplay(schedule)}</TableCell>
                <TableCell>
                  <ScheduleSourceCell schedule={schedule} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                    {schedule.created_by || "-"}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit schedule">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => openEdit(schedule)}
                        disabled={
                          schedule.status === "queued" ||
                          schedule.status === "running" ||
                          schedule.status === "completed" ||
                          schedule.status === "canceled"
                        }
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Cancel schedule">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => cancel(schedule)}
                        disabled={
                          schedule.status !== "scheduled" &&
                          schedule.status !== "queued" &&
                          schedule.status !== "failed"
                        }
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
              {canExpandSchedule && expandedScheduleId === String(schedule.id) ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ bgcolor: "#FAFAFA", p: 0 }}>
                    <Stack spacing={1.5} sx={{ px: 3, py: 2 }}>
                      {(schedule.actions || []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No actions have been added.
                        </Typography>
                      ) : (
                        (schedule.actions || []).map((action, index) => (
                          <ScheduleActionListItem
                            key={String(action.id || `${schedule.id}-${index}`)}
                            action={action}
                            index={index}
                          />
                        ))
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : null}
              </React.Fragment>
              );
            })}
            {visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" sx={{ p: 3, textAlign: "center", fontFamily: "var(--theo-font-sans)" }}>
                    {loading ? "Loading schedules..." : "No schedules in this view."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700 }}>
          {editing ? "Edit schedule" : "New schedule"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              multiline
              minRows={2}
              fullWidth
              sx={fieldSx}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Run at"
                type="datetime-local"
                value={form.scheduled_for}
                onChange={(e) => setForm((prev) => ({ ...prev, scheduled_for: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
              />
              <Autocomplete
                options={timezoneOptions}
                value={form.timezone}
                onChange={(_event, value) => setForm((prev) => ({ ...prev, timezone: value || getDefaultTimeZone() }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Timezone"
                    fullWidth
                    sx={fieldSx}
                  />
                )}
                fullWidth
              />
            </Stack>

            {editing && !hideHitlActionEditor ? (
              <>
                <Divider />
                <Typography sx={{ fontWeight: 700, fontFamily: "var(--theo-font-sans)" }}>Actions</Typography>
                {form.actions.length === 0 ? (
                  <Typography color="text.secondary" sx={{ fontFamily: "var(--theo-font-sans)" }}>
                    No actions scheduled yet.
                  </Typography>
                ) : (
                  form.actions.map((action, index) => (
                    <ScheduleActionAccordion
                      key={action.id || action.source_call_id || index}
                      action={action}
                      index={index}
                      summaryEnd={
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          onClick={(event) => event.stopPropagation()}
                          onFocus={(event) => event.stopPropagation()}
                        >
                          <ScheduleActionSourceIcon action={action} />
                          <Tooltip title="Move up">
                            <span>
                              <IconButton
                                size="small"
                                disabled={index === 0}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  moveAction(index, -1);
                                }}
                              >
                                <KeyboardArrowUpIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move down">
                            <span>
                              <IconButton
                                size="small"
                                disabled={index === form.actions.length - 1}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  moveAction(index, 1);
                                }}
                              >
                                <KeyboardArrowDownIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Remove">
                            <span>
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  removeAction(index);
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      }
                    >
                        <HitlActionParamsEditor
                          args={action.params_json || {}}
                          argTypes={action.arg_types_json || {}}
                          resolvedArgs={action.resolved_args || {}}
                          onChange={(params) => updateActionParams(index, params)}
                          fontSizes={{ body: 13 }}
                          referenceOptions={buildReferenceOptions(form.actions, index)}
                        />
                    </ScheduleActionAccordion>
                  ))
                )}
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: "none" }}>Close</Button>
          <Button variant="contained" onClick={save} disabled={saving || !form.title || !form.scheduled_for} sx={{ textTransform: "none" }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Schedules;
